/**
 * team-roles-api — the Roles screen's data layer: the tenant's role
 * definitions from the identity team surface.
 *
 * Four contract operations (api-contracts settings.ts):
 *   listTeamRoles   GET    /admin/team/roles            identity.team.read
 *   createTeamRole  POST   /admin/team/roles            identity.team.manage
 *   updateTeamRole  PATCH  /admin/team/roles/{roleId}   identity.team.manage
 *   deleteTeamRole  DELETE /admin/team/roles/{roleId}   identity.team.manage
 *
 * The typed client does not wrap these four yet, so they go through `apiRaw`
 * with their contract call ids — the same choke point collections-api uses.
 *
 * The SERVER is the only record. Built-in roles come back read-only
 * (`isCustom: false`); a custom role can only narrow its built-in base, and the
 * backend refuses anything else. Nothing here re-implements those rules or
 * keeps a local copy that could outlive a refusal: every write is followed by a
 * read of what the backend actually stored, and a refused write changes nothing.
 *
 * TENANT FENCING. Every read AND every write captures the tenant generation
 * before it awaits. A response — or a refusal — that arrives after the tenant
 * changed belongs to the tenant being left: it publishes nothing, starts no
 * reload, and resolves `{ status: "stale" }`. Stale is not a server refusal and
 * is never thrown, so a caller cannot show an old tenant's role name, success,
 * or error under the new tenant's chrome by accident.
 */
import { useEffect, useState } from "react";
import { apiRaw, isApiError } from "@v0y4ge/api-client";
import type { CustomRoleInput, RoleDefinition } from "@v0y4ge/api-contracts/dist/settings.js";
import { onTenantChange, onTenantSwitching } from "../shell/access";

export type TeamRole = RoleDefinition;

/** A write either completed for the tenant that is still active, or it
 *  belonged to a tenant that has since been switched away from. */
export type TeamRoleMutationResult =
  | { status: "done"; role: TeamRole | null }
  | { status: "stale" };

let ROLES: TeamRole[] | null = null;
let ERROR: string | null = null;
let loading = false;
/* Bumped when the tenant starts switching, so nothing that was sent for the
   tenant being left can land under the next tenant's chrome. */
let generation = 0;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());
const STALE: TeamRoleMutationResult = { status: "stale" };

/** The backend's own sentence for a refused call (name taken, role still
 *  assigned, capability outside the base role…), never a generic replacement. */
export function teamRoleErrorMessage(error: unknown): string {
  if (isApiError(error) || error instanceof Error) return error.message;
  return String(error);
}

async function loadRoles(): Promise<void> {
  const gen = generation;
  loading = true;
  try {
    const response = await apiRaw<{ roles?: TeamRole[] }>("/admin/team/roles", undefined, "listTeamRoles");
    if (gen !== generation) return;
    ROLES = Array.isArray(response?.roles) ? response.roles : [];
    ERROR = null;
  } catch (error) {
    if (gen !== generation) return;
    ERROR = teamRoleErrorMessage(error);
  } finally {
    if (gen === generation) {
      loading = false;
      notify();
    }
  }
}

onTenantSwitching(() => { generation += 1; ROLES = null; ERROR = null; loading = false; notify(); });
onTenantChange(() => { if (listeners.size) void loadRoles(); });

export function useTeamRoles(): { roles: TeamRole[]; loading: boolean; error: string | null; reload: () => void } {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    if (ROLES === null && !loading) void loadRoles();
    return () => { listeners.delete(fn); };
  }, []);
  return {
    roles: ROLES ?? [],
    loading: ROLES === null && ERROR === null,
    error: ERROR,
    reload: () => { ERROR = null; notify(); void loadRoles(); },
  };
}

/** One fenced write: send, then — only if the tenant is unchanged — apply the
 *  server's answer. A refusal for the current tenant is rethrown untouched; a
 *  response or refusal for a previous tenant resolves stale. */
async function fencedWrite<T>(
  send: () => Promise<T>,
  apply: (response: T) => Promise<void> | void,
  roleOf: (response: T) => TeamRole | null,
): Promise<TeamRoleMutationResult> {
  const gen = generation;
  let response: T;
  try {
    response = await send();
  } catch (error) {
    if (gen !== generation) return STALE;
    throw error;
  }
  if (gen !== generation) return STALE;
  await apply(response);
  if (gen !== generation) return STALE;
  return { status: "done", role: roleOf(response) };
}

/** Create a custom role. The create response carries the full list, so that
 *  list — the server's — replaces what the screen shows. */
export function createTeamRoleOp(input: CustomRoleInput): Promise<TeamRoleMutationResult> {
  return fencedWrite(
    () => apiRaw<{ role: TeamRole | null; roles: TeamRole[] }>(
      "/admin/team/roles",
      { method: "POST", body: JSON.stringify(input) },
      "createTeamRole",
    ),
    async (response) => {
      if (Array.isArray(response?.roles)) {
        ROLES = response.roles;
        ERROR = null;
        notify();
      } else {
        await loadRoles();
      }
    },
    (response) => response?.role ?? null,
  );
}

/** Rename a custom role or change which of its base role's capabilities it holds. */
export function updateTeamRoleOp(roleId: string, input: Partial<CustomRoleInput>): Promise<TeamRoleMutationResult> {
  return fencedWrite(
    () => apiRaw<{ role: TeamRole | null }>(
      `/admin/team/roles/${encodeURIComponent(roleId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
      "updateTeamRole",
    ),
    () => loadRoles(),
    (response) => response?.role ?? null,
  );
}

/** Delete a custom role. The backend refuses while anyone still holds it. */
export function deleteTeamRoleOp(roleId: string): Promise<TeamRoleMutationResult> {
  return fencedWrite(
    () => apiRaw<{ ok: true; roleId: string }>(
      `/admin/team/roles/${encodeURIComponent(roleId)}`,
      { method: "DELETE" },
      "deleteTeamRole",
    ),
    () => loadRoles(),
    () => null,
  );
}
